import { FunctionComponent, useState } from 'react'
import {
  ArrayLocationAPIResponse,
  ProjectSummary,
} from '@black-bear-energy/black-bear-energy-common'
import {
  Box,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  rucksack,
} from '@black-bear-energy/rucksack'
import { ExpandMore } from '@mui/icons-material'
import { alpha } from '@mui/material/styles'
import {
  ArrayLocationChip,
  getArrayLocationLabel,
} from '../common/ArrayLocationChip'
import { SiteRFPPhaseChip } from '../common/SiteRFPPhaseChip'
import { getSiteRFPPhaseLabel } from '../helpers/getSiteRFPPhaseLabel'
import { TableCellWithDefault } from '../common/TableCellWithDefault'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { getContractTypeLabel } from '../helpers/getContractTypeLabel'
import { handlePhase } from '../helpers/handlePhase'
import { PROJECT_SUMMARY_COLUMNS } from '../helpers/Dashboard/ProjectsTable/tableConstants'
import { SiteRFPRow } from './SiteRFPRow'
import { formatSignificantFigures } from 'components/helpers/formatSignificantFigures'

const MAX_VISIBLE_ARRAY_LOCATION_CHIPS = 4
const MAX_VISIBLE_PHASE_CHIPS = 4

export const CustomerRFPRow: FunctionComponent<CustomerRFPRowProps> = ({
  project,
  search,
}) => {
  const [toggleOpen, setToggleOpen] = useState(false)

  // expand the section if the user is searching
  const open = toggleOpen || !!search

  const phaseChips = [
    ...new Set(project.phases.flatMap((phase) => handlePhase(phase))),
  ]

  const sitesPath = `/sites?search=${encodeURIComponent(
    project.customerRFPName
  )}`

  return (
    <>
      <TableRow
        sx={{
          height: '46px',
          ...(open && {
            backgroundColor: (theme) =>
              alpha(theme.palette.action.selected, 0.04),
          }),
          '& .MuiTableCell-root': {
            textAlign: 'left',
            padding: '3px 16px',
          },
          // The chevron column is sized to fit the button without side padding.
          '& .MuiTableCell-root:first-of-type': {
            textAlign: 'center',
            padding: '3px 0',
          },
        }}
      >
        <TableCell>
          <IconButton onClick={() => setToggleOpen(!toggleOpen)}>
            <ExpandMore
              sx={{
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <Link
            href={sitesPath}
            target="_blank"
            rel="noreferrer"
            underline="hover"
            sx={{
              // Override the pink hover and focus color defined in index.css.
              '&:hover, &:focus': {
                color: rucksack.palette.primary.main,
              },
            }}
          >
            <TextWithTooltip
              text={project.customerRFPName}
              variant="body2"
              maxLineCount={2}
              searchString={search}
            />
          </Link>
        </TableCell>
        <TableCell>
          {formatSignificantFigures(project.totalSystemSize) + ' MW'}
        </TableCell>
        <TableCellWithDefault>
          {project.utilities.length > 0 && (
            <TextWithTooltip
              text={project.utilities.join(', ')}
              variant="body2"
              maxLineCount={2}
              searchString={search}
            />
          )}
        </TableCellWithDefault>
        <TableCellWithDefault>
          {project.arrayLocations.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {project.arrayLocations
                .slice(0, MAX_VISIBLE_ARRAY_LOCATION_CHIPS)
                .map((arrayLocation) => (
                  <ArrayLocationChip
                    key={arrayLocation}
                    arrayLocation={arrayLocation as ArrayLocationAPIResponse}
                    scale="mini"
                  />
                ))}
              {project.arrayLocations.length >
                MAX_VISIBLE_ARRAY_LOCATION_CHIPS && (
                <Tooltip
                  title={project.arrayLocations
                    .slice(MAX_VISIBLE_ARRAY_LOCATION_CHIPS)
                    .map((arrayLocation) => {
                      return getArrayLocationLabel(
                        arrayLocation as ArrayLocationAPIResponse
                      )
                    })
                    .join(', ')}
                >
                  <Typography variant="body2">
                    +
                    {project.arrayLocations.length -
                      MAX_VISIBLE_ARRAY_LOCATION_CHIPS}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )}
        </TableCellWithDefault>
        <TableCellWithDefault>
          {phaseChips.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
              }}
            >
              {phaseChips.slice(0, MAX_VISIBLE_PHASE_CHIPS).map((phase) => (
                <SiteRFPPhaseChip
                  key={phase}
                  phase={phase}
                  scale="mini"
                  labelVariant="integer"
                />
              ))}
              {phaseChips.length > MAX_VISIBLE_PHASE_CHIPS && (
                <Tooltip
                  title={phaseChips
                    .slice(MAX_VISIBLE_PHASE_CHIPS)
                    .map(
                      (phase) =>
                        `${getSiteRFPPhaseLabel(
                          phase,
                          'mini',
                          'integer'
                        )}: ${getSiteRFPPhaseLabel(phase, 'medium')}`
                    )
                    .join(', ')}
                >
                  <Typography variant="body2">
                    +{phaseChips.length - MAX_VISIBLE_PHASE_CHIPS}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          )}
        </TableCellWithDefault>
        <TableCellWithDefault>
          {getContractTypeLabel(project.contractType)}
        </TableCellWithDefault>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={PROJECT_SUMMARY_COLUMNS.length}
          sx={{ padding: 0, ...(!open && { border: 0 }) }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                {PROJECT_SUMMARY_COLUMNS.map((column) => (
                  <col
                    key={column.title}
                    style={{ width: `${column.width}%` }}
                  />
                ))}
              </colgroup>
              <TableBody>
                {project.siteRFPs.map((site, index) => (
                  <SiteRFPRow
                    key={site.siteRFPId}
                    site={site}
                    isLastSite={index === project.siteRFPs.length - 1}
                    search={search}
                  />
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

interface CustomerRFPRowProps {
  project: ProjectSummary
  search?: string
}
